// STEP 1: Add this OTP component at the TOP of your Dispatch.jsx file (after imports)

import { useEffect, useRef, useState } from "react";

export const OTPModal = ({ 
  isOpen, 
  onClose, 
  onVerify, 
  error = '', 
  loading = false,
  phoneNumber = ''
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '', '', '']);
      setFocusedIndex(0);
      setTimer(60);
      setCanResend(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    let interval;
    if (timer > 0 && !canResend) {
      interval = setInterval(() => {
        setTimer(prev => prev <= 1 ? (setCanResend(true), 0) : prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, canResend]);

  const handleInputChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        setFocusedIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleVerify = () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      onVerify(otpString);
    }
  };

  const isComplete = otp.every(digit => digit !== '');

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '2rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        <h2 style={{ color: '#1a1a1a', marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: '600', textAlign: 'center' }}>
          Corporate Payment: OTP Verification
        </h2>
        <p style={{ color: '#666', marginBottom: '2rem', textAlign: 'center', lineHeight: '1.5' }}>
          We've sent a 6-digit verification code to<br />
          <strong>{phoneNumber || 'your registered phone number'}</strong>
        </p>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={() => setFocusedIndex(index)}
              disabled={loading}
              autoComplete="off"
              style={{
                width: '50px',
                height: '50px',
                border: `2px solid ${error ? '#ef4444' : focusedIndex === index ? '#3b82f6' : '#e5e7eb'}`,
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a1a1a',
                background: error ? '#fef2f2' : 'white',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{ color: '#ef4444', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1rem', minHeight: '1.25rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '500',
              fontSize: '0.9rem',
              cursor: 'pointer',
              border: 'none',
              background: '#f3f4f6',
              color: '#374151',
              opacity: loading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={!isComplete || loading}
            style={{
              flex: 1,
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: '500',
              fontSize: '0.9rem',
              cursor: 'pointer',
              border: 'none',
              background: '#3b82f6',
              color: 'white',
              opacity: (!isComplete || loading) ? 0.6 : 1
            }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Didn't receive the code?
          </span>
          {canResend ? (
            <button style={{
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'underline',
              marginLeft: '0.25rem'
            }}>
              Resend Code
            </button>
          ) : (
            <span style={{ color: '#ef4444', fontWeight: '500', marginLeft: '0.25rem' }}>
              Resend in {timer}s
            </span>
          )}
        </div>
      </div>
    </div>
  );
};